import { useInfiniteQueryChats } from '@/hooks/query/useInfiniteQueryChats';
import { filterChatsForWorkAvailability } from '@heidi/agents/contracts';
import { useCanUseWorkHome } from '@heidi/agents/data';
import { useMemo } from 'react';
import { useRecentVisits } from '../recent-visits/use-recent-visits';
import { mergeRecentItems } from './merge-recent-items';

export type RecentItemsAvailability = 'unknown' | 'empty' | 'has-items';

export interface UseRecentItemsSourceOptions {
  searchQuery?: string;
  isSearching?: boolean;
  visitsEnabled?: boolean;
  chatsEnabled?: boolean;
  pollVisits?: boolean;
}

/**
 * Canonical data source for the mixed Recents collection. Both the sidebar
 * section and the Home hover probe use this hook so work availability,
 * pinned-chat exclusion, pending-delete filtering, placeholder handling,
 * merging, and empty-state availability cannot drift apart.
 */
export function useRecentItemsSource({
  searchQuery = '',
  isSearching = false,
  visitsEnabled = true,
  chatsEnabled = true,
  pollVisits = true,
}: UseRecentItemsSourceOptions = {}) {
  const recentVisits = useRecentVisits('createdAt', {
    searchQuery,
    enabled: visitsEnabled,
    pollEnabled: pollVisits,
  });
  const isWorkEnabled = useCanUseWorkHome();
  const recentChats = useInfiniteQueryChats({
    query: searchQuery || undefined,
    enabled: chatsEnabled,
  });

  const chats = useMemo(() => {
    const workFiltered = filterChatsForWorkAvailability(
      recentChats.data?.pages.flatMap((page) => page.chats) ?? [],
      isWorkEnabled
    );
    return isSearching ? workFiltered : workFiltered.filter((chat) => !chat.is_saved);
  }, [recentChats.data?.pages, isWorkEnabled, isSearching]);

  const items = useMemo(() => {
    const effectiveVisits = recentVisits.isPlaceholderData ? [] : recentVisits.visits;
    return mergeRecentItems(effectiveVisits, chats);
  }, [recentVisits.isPlaceholderData, recentVisits.visits, chats]);

  const bothSourcesSettled =
    (recentVisits.hasResolvedData || recentVisits.isError) &&
    (recentChats.data !== undefined || recentChats.isError) &&
    !recentVisits.isLoading &&
    !recentVisits.isPlaceholderData &&
    !recentChats.isLoading;
  const hasNextPage =
    Boolean(recentVisits.hasNextPage) || Boolean(recentChats.hasNextPage);
  const availability: RecentItemsAvailability =
    items.length > 0
      ? 'has-items'
      : bothSourcesSettled && !hasNextPage
        ? 'empty'
        : 'unknown';

  return {
    ...recentVisits,
    data: recentChats.data,
    chats,
    items,
    availability,
    isLoadingChats: recentChats.isLoading,
    isFetchingChats: recentChats.isFetching,
    isErrorChats: recentChats.isError,
    hasNextPageChats: recentChats.hasNextPage,
    isFetchingNextPageChats: recentChats.isFetchingNextPage,
    fetchNextPageChats: recentChats.fetchNextPage,
    refetchChats: recentChats.refetch,
  };
}
